import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import { FC, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  LockOutlined as LockIcon,
  EmailOutlined as EmailIcon,
  ArrowForward as ArrowIcon,
} from "@mui/icons-material";
import useAuth from "../../../hooks/useAuth";
import { LoginFormValidation } from "./LoginFormValidation";

const Login: FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const { message } = (location.state as any) || {};

  const form = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: LoginFormValidation,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async ({ email, password }) => {
      setLoading(true);
      setError("");
      try {
        if (login) await login(email, password);
        navigate("/dashboard");
      } catch (err: any) {
        setError(
          err?.response?.data?.detail ||
            err?.message ||
            "Invalid credentials. Please try again."
        );
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: { xs: "center", md: "flex-start" },
        height: "100%",
        px: { xs: 3, sm: 6, md: 7, lg: 9 },
        py: { xs: 5, md: 0 },
        // Entrance animation
        "@keyframes slideUp": {
          from: { opacity: 0, transform: "translateY(24px)" },
          to:   { opacity: 1, transform: "translateY(0)" },
        },
        animation: "slideUp 0.55s cubic-bezier(0.22, 1, 0.36, 1) both",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 400 }}>

        {/* Logo — blue version on white background */}
        <Box sx={{ mb: 5 }}>
          <Box
            component="img"
            src="/everpesa_logo_blue.png"
            alt="Everpesa"
            sx={{
              height: 30,
              width: "auto",
              objectFit: "contain",
              display: "block",
            }}
          />
        </Box>

        {/* Heading */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            fontWeight={800}
            sx={{
              lineHeight: 1.15,
              letterSpacing: "-0.03em",
              color: "text.primary",
              mb: 1,
            }}
          >
            Welcome back
          </Typography>
          <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6 }}>
            Sign in to your admin account to continue managing the platform.
          </Typography>
        </Box>

        {/* Success message */}
        {message && (
          <Box
            sx={{
              mb: 3,
              px: 2,
              py: 1.5,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: "1px solid",
              borderColor: alpha(theme.palette.success.main, 0.25),
            }}
          >
            <Typography variant="body2" color="success.main" fontWeight={500}>
              {message}
            </Typography>
          </Box>
        )}

        {/* Error message */}
        {error && (
          <Box
            sx={{
              mb: 3,
              px: 2,
              py: 1.5,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.error.main, 0.07),
              border: "1px solid",
              borderColor: alpha(theme.palette.error.main, 0.25),
              "@keyframes shake": {
                "0%, 100%": { transform: "translateX(0)" },
                "20%, 60%":  { transform: "translateX(-6px)" },
                "40%, 80%":  { transform: "translateX(6px)" },
              },
              animation: "shake 0.4s ease both",
            }}
          >
            <Typography variant="body2" color="error.main" fontWeight={500}>
              {error}
            </Typography>
          </Box>
        )}

        {/* Form */}
        <form onSubmit={form.handleSubmit} noValidate>
          <Stack spacing={2.5}>
            {/* Email */}
            <TextField
              fullWidth
              name="email"
              type="email"
              label="Email address"
              autoComplete="email"
              autoFocus
              value={form.values.email}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              error={Boolean(form.touched.email && form.errors.email)}
              helperText={form.touched.email && form.errors.email}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon
                      sx={{
                        fontSize: 18,
                        color: form.touched.email && !form.errors.email
                          ? "success.main"
                          : "text.disabled",
                        transition: "color 0.2s",
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  transition: "box-shadow 0.2s",
                  "&.Mui-focused": {
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
                  },
                },
              }}
            />

            {/* Password */}
            <TextField
              fullWidth
              name="password"
              type={showPassword ? "text" : "password"}
              label="Password"
              autoComplete="current-password"
              value={form.values.password}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              error={Boolean(form.touched.password && form.errors.password)}
              helperText={form.touched.password && form.errors.password}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon
                      sx={{
                        fontSize: 18,
                        color: form.touched.password && !form.errors.password
                          ? "success.main"
                          : "text.disabled",
                        transition: "color 0.2s",
                      }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      size="small"
                      edge="end"
                      tabIndex={-1}
                      sx={{ color: "text.disabled" }}
                    >
                      {showPassword ? (
                        <HideIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <ShowIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  transition: "box-shadow 0.2s",
                  "&.Mui-focused": {
                    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
                  },
                },
              }}
            />

            {/* Submit */}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={loading}
              size="large"
              endIcon={
                loading ? (
                  <CircularProgress size={17} color="inherit" />
                ) : (
                  <ArrowIcon sx={{ fontSize: 18 }} />
                )
              }
              sx={{
                mt: 0.5,
                py: 1.5,
                borderRadius: 1.5,
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: "0.01em",
                textTransform: "none",
                boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                  transform: "translateY(-1px)",
                },
                "&:active": { transform: "translateY(0)" },
                "&.Mui-disabled": { boxShadow: "none" },
              }}
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </Stack>
        </form>

        {/* Footer */}
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{
            display: "block",
            textAlign: "center",
            mt: 4,
            lineHeight: 1.7,
          }}
        >
          Everpesa Admin Portal · Authorised access only
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;